import React from 'react';
import { View, ActivityIndicator, StyleSheet, TouchableOpacity } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../../context/ThemeContext';
import { Typography } from './Typography';
import { useI18n } from '../../i18n';

import { ACCENT_TEXT } from '../../styles/accentText';
export type ScreenStateStatus = 'loading' | 'error' | 'empty';

interface ScreenStateProps {
    status: ScreenStateStatus;
    /** Optional message override; defaults to a sensible i18n string per status. */
    message?: string;
    /** Optional secondary description shown below the message. */
    description?: string;
    /** Optional icon override for `empty` (Ionicons name). Default: `leaf-outline`. */
    icon?: keyof typeof Ionicons.glyphMap;
    /** Optional action button — usually `Retry` for errors, `Refresh` for empty. */
    actionLabel?: string;
    onAction?: () => void;
    /** Optional second, quieter action under the main one — e.g. «На главную»
     *  on the app-level error screen (CORE-1517). */
    secondaryActionLabel?: string;
    onSecondaryAction?: () => void;
    /** Inline mode renders without taking the full screen — useful inside cards. */
    inline?: boolean;
}

/**
 * Shared loading / error / empty state. Standardises the look across screens
 * that previously rendered ad-hoc spinners + custom empty cards.
 *
 * Themed via useTheme — spinner is solar, copy is textPrimary/textMuted.
 */
export const ScreenState: React.FC<ScreenStateProps> = ({
    status,
    message,
    description,
    icon,
    actionLabel,
    onAction,
    secondaryActionLabel,
    onSecondaryAction,
    inline = false,
}) => {
    const { colors } = useTheme();
    const { t } = useI18n('common');

    const defaultMessages: Record<ScreenStateStatus, string> = {
        loading: t('state.loading', 'Загрузка…'),
        error: t('state.error', 'Что-то пошло не так'),
        empty: t('state.empty', 'Пока пусто'),
    };
    const defaultIcons: Record<ScreenStateStatus, keyof typeof Ionicons.glyphMap> = {
        loading: 'sync-outline',
        error: 'cloud-offline-outline',
        empty: 'leaf-outline',
    };

    const wrapperStyle = [
        styles.wrapper,
        inline ? styles.inline : styles.full,
        // CORE-1733 (возврат QA 23.10) — полноэкранное состояние на общем фоне v4 (pageBg на
        // вебе прозрачный, на нативе = canvas), а не сплошной #FFFFFF.
        { backgroundColor: inline ? 'transparent' : colors.pageBg },
    ];

    return (
        <View style={wrapperStyle} accessibilityRole={status === 'loading' ? 'progressbar' : 'alert'}>
            {status === 'loading' ? (
                <ActivityIndicator size="large" color={ACCENT_TEXT} />
            ) : (
                <Ionicons
                    name={icon ?? defaultIcons[status]}
                    size={inline ? 32 : 48}
                    color={colors.textMuted}
                    style={styles.icon}
                />
            )}
            <Typography variant="h5" align="center" style={styles.message} color={colors.textPrimary}>
                {message ?? defaultMessages[status]}
            </Typography>
            {description ? (
                <Typography variant="caption" align="center" color={colors.textMuted} style={styles.description}>
                    {description}
                </Typography>
            ) : null}
            {actionLabel && onAction ? (
                <TouchableOpacity
                    onPress={onAction}
                    activeOpacity={0.7}
                    style={[styles.action, { borderColor: colors.solar }]}
                    accessibilityRole="button"
                    accessibilityLabel={actionLabel}
                >
                    <Typography variant="button" color={ACCENT_TEXT}>
                        {actionLabel}
                    </Typography>
                </TouchableOpacity>
            ) : null}
            {secondaryActionLabel && onSecondaryAction ? (
                <TouchableOpacity
                    onPress={onSecondaryAction}
                    activeOpacity={0.7}
                    style={styles.secondaryAction}
                    accessibilityRole="button"
                    accessibilityLabel={secondaryActionLabel}
                >
                    <Typography variant="button" color={colors.textMuted}>
                        {secondaryActionLabel}
                    </Typography>
                </TouchableOpacity>
            ) : null}
        </View>
    );
};

const styles = StyleSheet.create({
    wrapper: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 24,
    },
    full: {
        flex: 1,
        minHeight: 320,
    },
    inline: {
        paddingVertical: 32,
    },
    icon: {
        marginBottom: 12,
    },
    message: {
        marginTop: 12,
        maxWidth: 480,
    },
    description: {
        marginTop: 6,
        maxWidth: 420,
    },
    action: {
        marginTop: 20,
        paddingHorizontal: 24,
        paddingVertical: 10,
        borderRadius: 100,
        borderWidth: 1.5,
    },
    secondaryAction: {
        marginTop: 12,
        paddingHorizontal: 24,
        paddingVertical: 10,
    },
});
